import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Calendar, 
  MapPin, 
  Clock, 
  Users, 
  ArrowLeft,
  Share2,
  Heart,
  Star,
  Phone,
  Mail,
  Globe,
  X,
  CheckCircle,
  Ticket,
  Copy,
  Check,
  ShoppingCart,
  CreditCard,
  ExternalLink,
  MessageCircle,
  Download,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCelebration } from '../contexts/CelebrationContext';
import toast from 'react-hot-toast';
import api from '../config/api';
import FeaturedGallery from '../components/common/FeaturedGallery';
import { isEventDatePast, formatEventDateRange, parseDateString } from '../utils/dateUtils';
import { createEventSlug, extractCity, extractYear, getProductionBaseUrl } from '../utils/slugUtils';

const SQUARE_SCRIPTS = {
  sandbox: 'https://sandbox.web.squarecdn.com/v1/square.js',
  production: 'https://web.squarecdn.com/v1/square.js',
};

function loadSquareScript(environment) {
  const env = environment === 'production' ? 'production' : 'sandbox';
  const src = SQUARE_SCRIPTS[env];

  return new Promise((resolve, reject) => {
    if (window.Square) return resolve();

    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Square script')));
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error('Failed to load Square script')));
    document.head.appendChild(script);
  });
}

// Embedded Zeffy Registration Modal component
const ZeffyRegistrationModal = ({ event, onClose }) => {
  const rawZeffyUrl = event?.zeffyUrl || event?.registrationUrl || event?.externalRegistrationUrl || 'https://www.zeffy.com/en-US/ticketing/washington-ganesh-festival--2026';
  
  // Convert Zeffy ticketing URL to embed format if needed
  const zeffyEmbedUrl = rawZeffyUrl.includes('/ticketing/')
    ? rawZeffyUrl.replace('/ticketing/', '/embed/ticketing/')
    : rawZeffyUrl;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto animate-fade-in">
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl max-w-4xl w-full overflow-hidden border border-gray-100 flex flex-col my-auto max-h-[92vh]">
        {/* Header */}
        <div className="p-4 sm:p-5 bg-gradient-to-r from-gray-900 via-primary-950 to-gray-900 text-white flex items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-primary-600 rounded-xl flex items-center justify-center font-bold text-white shadow-md flex-shrink-0">
              🎟️
            </div>
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-bold truncate">{event.title} Registration</h3>
              <p className="text-xs text-primary-200 truncate">100% Free &amp; Secure Non-Profit Registration via Zeffy</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href={rawZeffyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded-lg transition-colors"
            >
              <span>Open in New Tab</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button
              onClick={onClose}
              className="p-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
              aria-label="Close registration modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Embedded Iframe Body */}
        <div className="p-2 sm:p-4 flex-1 overflow-y-auto bg-gray-50">
          <iframe
            title={`${event.title} Registration`}
            src={zeffyEmbedUrl}
            className="w-full min-h-[600px] h-full border-0 rounded-xl bg-white shadow-inner"
            allow="payment; payment-request"
            allowpaymentrequest="true"
          />
        </div>
      </div>
    </div>
  );
};

// Square-enabled registration form component
const SquareRegistrationForm = ({ event, currentUser, registrationData, setRegistrationData, onRegister, isRegistering, onClose, selectedAddons, eventAddons }) => {
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [squareReady, setSquareReady] = useState(false);
  const [squareInitError, setSquareInitError] = useState(null);
  const [totalAmountCents, setTotalAmountCents] = useState(0);
  const [applePayAvailable, setApplePayAvailable] = useState(false);

  const cardRef = useRef(null);
  const paymentsRef = useRef(null);
  const applePayRef = useRef(null);
  const applePayBtnRef = useRef(null);
  const payContextRef = useRef({});
  const containerId = useMemo(
    () => `square-card-container-${Math.random().toString(36).slice(2)}`,
    []
  );

  /* Stripe payment intent + wallet flow removed (migrated to Square)
  useEffect(() => {
    // Create payment intent for paid events OR if there are paid addons
    if (event && (isPaidEvent(event.price) || hasPaidAddons(selectedAddons, eventAddons))) {
      createPaymentIntent();
    }
  }, [event, selectedAddons, eventAddons]);

  const createPaymentIntent = async () => {
    try {
      const unitPrice = parseEventPrice(event.price) * 100; // Convert to cents
      let totalAmount = unitPrice * registrationData.quantity; // Multiply by quantity
      
      // Add addon prices (excluding free items)
      const addonsArray = [];
      if (selectedAddons && eventAddons) {
        Object.keys(selectedAddons).forEach(addonId => {
          const quantity = selectedAddons[addonId];
          if (quantity > 0) {
            const addon = eventAddons.find(a => a.id === addonId);
            if (addon && !addon.isFreeWithTicket) {
              totalAmount += (addon.price * 100 * quantity); // Convert to cents
              addonsArray.push({ addonId, quantity });
            } else if (addon && addon.isFreeWithTicket) {
              // Include free addons in array for tracking
              addonsArray.push({ addonId, quantity });
            }
          }
        });
      }
      
      // If total amount is 0, don't create payment intent
      if (totalAmount === 0) {
        console.log('[Payment] Total amount is 0, skipping payment intent creation');
        setClientSecret('');
        return;
      }
      
      // Track total for Payment Request button
      setTotalAmountCents(Math.round(totalAmount));
      
      const response = await api.post(`/events/${event.id}/create-payment-intent`, {
        amount: totalAmount,
        quantity: registrationData.quantity,
        userEmail: currentUser?.email || registrationData.email,
        userName: currentUser?.displayName || registrationData.name,
        userId: currentUser?.uid || currentUser?.id || `anon_${Date.now()}`,
        addons: addonsArray
      });

      if (response.ok) {
        const { clientSecret } = await response.json();
        setClientSecret(clientSecret);
      } else {
        toast.error('Failed to initialize payment. Please try again.');
      }
    } catch (error) {
      console.error('Error creating payment intent:', error);
      toast.error('Failed to initialize payment. Please try again.');
    }
  };

  // Initialize Payment Request (Apple Pay / Google Pay) when Stripe and client secret are ready
  useEffect(() => {
    const initPaymentRequest = async () => {
      if (!stripe || !clientSecret || totalAmountCents <= 0) return;

      const pr = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: event?.title || 'Event Registration',
          amount: totalAmountCents
        },
        requestPayerName: true,
        requestPayerEmail: true
      });

      const result = await pr.canMakePayment();
      if (result) {
        setPaymentRequest(pr);
        setCanUsePaymentRequest(true);

        pr.on('paymentmethod', async (ev) => {
          try {
            setPaymentLoading(true);

            // Use the payment method from the wallet to confirm the intent
            const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
              payment_method: ev.paymentMethod.id,
              payment_method_options: {
                card: {
                  request_three_d_secure: 'automatic'
                }
              },
              receipt_email: currentUser?.email || registrationData.email
            });

            if (error) {
              ev.complete('fail');
              toast.error(error.message || 'Payment failed. Please try again.');
              setPaymentLoading(false);
              return;
            }

            ev.complete('success');

            if (paymentIntent.status === 'succeeded') {
              await onRegister(paymentIntent.id);
              toast.success('Payment successful! Registration confirmation will be sent via email.');
            }
          } catch (err) {
            console.error('Payment Request error:', err);
            ev.complete('fail');
            toast.error('Payment failed. Please try again.');
          } finally {
            setPaymentLoading(false);
          }
        });
      } else {
        setCanUsePaymentRequest(false);
        setPaymentRequest(null);
      }
    };

    initPaymentRequest();
  }, [stripe, clientSecret, totalAmountCents, event, currentUser, registrationData]);
  */

  const isPaidEventValue = isPaidEvent(event.price);
  const hasPaidAddonsValue = hasPaidAddons(selectedAddons, eventAddons);
  const requiresPayment = isPaidEventValue || hasPaidAddonsValue;

  const calculateTotals = () => {
    const unitPriceCents = parseEventPrice(event.price) * 100;
    let totalAmount = unitPriceCents * (registrationData.quantity || 1);

    const addonsArray = [];
    if (selectedAddons && eventAddons) {
      Object.keys(selectedAddons).forEach((addonId) => {
        const quantity = selectedAddons[addonId];
        if (quantity > 0) {
          const addon = eventAddons.find((a) => a.id === addonId);
          if (addon && !addon.isFreeWithTicket) {
            totalAmount += addon.price * 100 * quantity;
            addonsArray.push({ addonId, quantity });
          } else if (addon && addon.isFreeWithTicket) {
            addonsArray.push({ addonId, quantity });
          }
        }
      });
    }

    return { totalAmountCents: Math.round(totalAmount), addonsArray };
  };

  payContextRef.current = {
    calculateTotals,
    event,
    currentUser,
    registrationData,
    onRegister,
  };

  const submitPaidWithSourceId = useCallback(async (sourceId) => {
    const ctx = payContextRef.current;
    const { totalAmountCents: totalAmount, addonsArray } = ctx.calculateTotals();
    if (!totalAmount || totalAmount <= 0) {
      toast.error('Invalid payment amount. Please try again.');
      throw new Error('Invalid payment amount');
    }
    const response = await api.post(`/events/${ctx.event.id}/create-payment`, {
      sourceId,
      amount: totalAmount,
      quantity: ctx.registrationData.quantity,
      userEmail: ctx.currentUser?.email || ctx.registrationData.email,
      userName: ctx.currentUser?.displayName || ctx.registrationData.name,
      userId: ctx.currentUser?.uid || ctx.currentUser?.id || `anon_${Date.now()}`,
      addons: addonsArray,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Payment failed. Please try again.');
    }
    if (data.paymentId) {
      await ctx.onRegister(data.paymentId);
      toast.success('Payment successful! Registration confirmation will be sent via email.');
    } else {
      throw new Error('Payment succeeded but no paymentId was returned.');
    }
  }, []);

  useEffect(() => {
    if (!event) return;
    if (!requiresPayment) {
      setTotalAmountCents(0);
      return;
    }
    const { totalAmountCents: total } = calculateTotals();
    setTotalAmountCents(total);
  }, [event, requiresPayment, registrationData.quantity, selectedAddons, eventAddons]);

  useEffect(() => {
    let cancelled = false;

    async function initSquare() {
      if (!requiresPayment) {
        setSquareReady(false);
        setSquareInitError(null);
        return;
      }

      try {
        setSquareInitError(null);
        setSquareReady(false);

        const cfgRes = await api.get('/square-config');
        const cfg = cfgRes.ok ? await cfgRes.json() : null;
        if (!cfgRes.ok || !cfg?.applicationId || !cfg?.locationId) {
          throw new Error(cfg?.error || 'Square configuration not available');
        }

        await loadSquareScript(cfg.environment);
        if (cancelled) return;

        const payments = window.Square.payments(cfg.applicationId, cfg.locationId);
        paymentsRef.current = payments;
        const card = await payments.card();
        cardRef.current = card;
        await card.attach(`#${containerId}`);
        if (cancelled) return;

        setSquareReady(true);
      } catch (e) {
        console.error('Square init error:', e);
        setSquareInitError(e.message || 'Failed to initialize Square');
      }
    }

    initSquare();

    return () => {
      cancelled = true;
      try {
        applePayRef.current?.destroy?.();
      } catch (e) {
        // ignore
      }
      applePayRef.current = null;
      try {
        cardRef.current?.destroy?.();
      } catch (e) {
        // ignore
      }
      cardRef.current = null;
      paymentsRef.current = null;
    };
  }, [requiresPayment, containerId]);

  useEffect(() => {
    let cancelled = false;
    async function setupApplePay() {
      try {
        applePayRef.current?.destroy?.();
      } catch (e) {
        // ignore
      }
      applePayRef.current = null;
      setApplePayAvailable(false);
      if (!requiresPayment || !squareReady || !paymentsRef.current) return;
      const cents = totalAmountCents;
      if (!cents || cents < 50) return;
      const dollars = (cents / 100).toFixed(2);
      try {
        const paymentRequest = paymentsRef.current.paymentRequest({
          countryCode: 'US',
          currencyCode: 'USD',
          total: { amount: dollars, label: event.title || 'Event registration' },
        });
        const ap = await paymentsRef.current.applePay(paymentRequest);
        if (cancelled) {
          ap.destroy?.();
          return;
        }
        applePayRef.current = ap;
        setApplePayAvailable(true);
      } catch (e) {
        console.debug('Apple Pay unavailable:', e?.message || e);
      }
    }
    setupApplePay();
    return () => {
      cancelled = true;
      try {
        applePayRef.current?.destroy?.();
      } catch (e) {
        // ignore
      }
      applePayRef.current = null;
    };
  }, [requiresPayment, squareReady, totalAmountCents, event?.title]);

  useEffect(() => {
    const btn = applePayBtnRef.current;
    if (!btn || !applePayAvailable) return;

    const onApplePayClick = async (e) => {
      e.preventDefault();
      const ap = applePayRef.current;
      if (!ap) return;
      if (!currentUser && (!registrationData.name || !registrationData.email)) {
        toast.error('Please enter your name and email.');
        return;
      }
      setPaymentLoading(true);
      try {
        const tokenizeResult = await ap.tokenize();
        if (tokenizeResult.status !== 'OK') {
          const message =
            tokenizeResult.errors?.[0]?.message ||
            'Apple Pay could not complete. Try again or use a card.';
          throw new Error(message);
        }
        await submitPaidWithSourceId(tokenizeResult.token);
      } catch (error) {
        console.error('Payment error:', error);
        toast.error(error.message || 'Payment failed. Please try again.');
      } finally {
        setPaymentLoading(false);
      }
    };

    btn.addEventListener('click', onApplePayClick);
    return () => btn.removeEventListener('click', onApplePayClick);
  }, [
    applePayAvailable,
    currentUser,
    registrationData.name,
    registrationData.email,
    submitPaidWithSourceId,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({});

    // Validate form fields
    const errors = {};
    
    if (!currentUser && (!registrationData.name || !registrationData.email)) {
      errors.general = 'Name and email are required.';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Check if payment is required (paid event OR paid addons)
    const requiresPayment = isPaidEvent(event.price) || hasPaidAddons(selectedAddons, eventAddons);
    
    if (requiresPayment) {
      // Handle paid registration with Stripe (for paid events or paid addons)
      await handlePaidRegistration();
    } else {
      // Handle free event registration (no paid items)
      await handleFreeRegistration();
    }
  };

  const handlePaidRegistration = async () => {
    if (!squareReady || !cardRef.current) {
      toast.error('Payment system is loading. Please wait a moment and try again.');
      return;
    }

    setPaymentLoading(true);

    try {
      const { totalAmountCents: totalAmount } = calculateTotals();
      if (!totalAmount || totalAmount <= 0) {
        toast.error('Invalid payment amount. Please try again.');
        return;
      }

      const tokenizeResult = await cardRef.current.tokenize();
      if (tokenizeResult.status !== 'OK') {
        const message =
          tokenizeResult.errors?.[0]?.message ||
          'Card information is invalid. Please check and try again.';
        throw new Error(message);
      }

      await submitPaidWithSourceId(tokenizeResult.token);
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Payment failed. Please try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleFreeRegistration = async () => {
    await onRegister();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-100 animate-in my-4">
        <div className="p-4 sm:p-5 md:p-6">
          <div className="flex justify-between items-start mb-4 sm:mb-5 md:mb-6">
            <div className="flex items-center min-w-0 flex-1 mr-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                <Ticket className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 break-words">Register for Event</h3>
                <p className="text-xs sm:text-sm text-gray-600 break-words truncate">{event.title}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex-shrink-0"
              aria-label="Close registration modal"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>

          {/* Donation Callout Banner inside Registration Modal */}
          <div className="mb-4 p-3 bg-gradient-to-r from-orange-500/10 via-red-500/10 to-amber-500/10 rounded-xl border border-orange-200 flex items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-2 min-w-0">
              <Heart className="w-5 h-5 text-orange-500 fill-current flex-shrink-0 animate-pulse" />
              <div>
                <p className="text-xs sm:text-sm font-bold text-gray-900 leading-tight">Support Beats of Washington</p>
                <p className="text-xs text-gray-600 truncate">Help us organize more community events</p>
              </div>
            </div>
            <a
              href={event?.donationUrl || event?.zeffyDonationUrl || "https://www.zeffy.com/en-US/donation-form/548a0956-2611-4a95-ac05-1178941e5f09"}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold rounded-lg hover:from-orange-600 hover:to-red-600 transition-all shadow hover:shadow-md flex-shrink-0 flex items-center gap-1"
            >
              <Heart className="w-3.5 h-3.5 fill-current" />
              <span>Donate</span>
            </a>
          </div>

           {requiresPayment && (
             <div className="mb-4 sm:mb-5 md:mb-6 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
               <div className="flex items-center mb-1.5 sm:mb-2">
                 <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-1.5 sm:mr-2 flex-shrink-0" />
                 <span className="text-sm sm:text-base font-semibold text-blue-900 break-words">Payment Required</span>
               </div>
               <p className="text-xs sm:text-sm text-blue-700 break-words">
                 {isPaidEventValue && hasPaidAddonsValue 
                   ? `Event ticket ($${parseEventPrice(event.price).toFixed(2)} per person) + selected items require payment`
                   : isPaidEventValue
                   ? `This event requires a payment of $${parseEventPrice(event.price).toFixed(2)} per person`
                   : 'Selected items require payment'}
               </p>
             </div>
           )}

           {/* Quantity Selector for Paid Events or when addons are selected */}
           {(isPaidEventValue || hasPaidAddonsValue) && (
             <div className="mb-4 sm:mb-5 md:mb-6 p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
               <div className="flex items-center mb-2 sm:mb-3">
                 <Users className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-1.5 sm:mr-2 flex-shrink-0" />
                 <span className="text-sm sm:text-base font-semibold text-green-900 break-words">Number of Attendees</span>
               </div>
               <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                 <label className="text-xs sm:text-sm text-gray-700 flex-shrink-0">Quantity:</label>
                 <select
                   value={registrationData.quantity}
                   onChange={(e) => setRegistrationData({...registrationData, quantity: parseInt(e.target.value)})}
                   className="px-2.5 sm:px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                 >
                   <option value={1}>1 person</option>
                   <option value={2}>2 people</option>
                   <option value={3}>3 people</option>
                   <option value={4}>4 people</option>
                   <option value={5}>5 people</option>
                   <option value={6}>6 people</option>
                   <option value={7}>7 people</option>
                   <option value={8}>8 people</option>
                   <option value={9}>9 people</option>
                   <option value={10}>10 people</option>
                 </select>
               </div>
                 <div className="mt-2 sm:mt-3 p-3 sm:p-4 bg-white rounded-lg border-2 border-green-200">
                   <div className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Order Summary</div>
                   
                   {/* Ticket Price */}
                   <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-200">
                     <div>
                       <span className="text-xs sm:text-sm text-gray-700">Event Ticket</span>
                       <div className="text-xs text-gray-500">
                         ${typeof event.price === 'number' ? event.price.toFixed(2) : (parseEventPrice(event.price).toFixed(2))} × {registrationData.quantity} {registrationData.quantity === 1 ? 'person' : 'people'}
                       </div>
                     </div>
                     <span className="text-sm sm:text-base font-semibold text-gray-900">
                       ${(parseEventPrice(event.price) * registrationData.quantity).toFixed(2)}
                     </span>
                   </div>

                   {/* Selected Addons */}
                   {selectedAddons && eventAddons && Object.keys(selectedAddons).some(id => selectedAddons[id] > 0) && (
                     <div className="mb-2 pb-2 border-b border-gray-200">
                       <div className="text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">Additional Items:</div>
                       {Object.keys(selectedAddons).map(addonId => {
                         const quantity = selectedAddons[addonId];
                         if (quantity === 0) return null;
                         const addon = eventAddons.find(a => a.id === addonId);
                         if (!addon) return null;
                         
                         // Show free items with a note
                         if (addon.isFreeWithTicket) {
                           return (
                             <div key={addonId} className="flex justify-between items-center mb-1 text-xs text-gray-600">
                               <div>
                                 <span>{addon.name} × {quantity}</span>
                                 <span className="ml-2 text-green-600 font-semibold">(FREE)</span>
                               </div>
                               <span className="text-green-600 font-semibold">$0.00</span>
                             </div>
                           );
                         }
                         
                         // Show paid items
                         return (
                           <div key={addonId} className="flex justify-between items-center mb-1 text-xs text-gray-700">
                             <div>
                               <span className="font-medium">{addon.name}</span>
                               <span className="text-gray-500 ml-1">× {quantity}</span>
                             </div>
                             <span className="font-semibold">${(addon.price * quantity).toFixed(2)}</span>
                           </div>
                         );
                       })}
                     </div>
                   )}

                   {/* Total Amount */}
                   <div className="flex justify-between items-center pt-2 border-t-2 border-green-300">
                     <span className="text-sm sm:text-base font-bold text-gray-900">Total Amount:</span>
                     <span className="text-lg sm:text-xl font-bold text-green-700">
                       ${(() => {
                         let total = parseEventPrice(event.price) * registrationData.quantity;
                         if (selectedAddons && eventAddons) {
                           Object.keys(selectedAddons).forEach(addonId => {
                             const quantity = selectedAddons[addonId];
                             if (quantity > 0) {
                               const addon = eventAddons.find(a => a.id === addonId);
                               if (addon && !addon.isFreeWithTicket) {
                                 total += addon.price * quantity;
                               }
                             }
                           });
                         }
                         return total.toFixed(2);
                       })()}
                     </span>
                   </div>
                 </div>
             </div>
           )}

          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            {currentUser ? (
              <>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={registrationData.name}
                    disabled
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-200 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={registrationData.email}
                    disabled
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-200 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={registrationData.name}
                    onChange={(e) => setRegistrationData({...registrationData, name: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={registrationData.email}
                    onChange={(e) => setRegistrationData({...registrationData, email: e.target.value})}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter your email address"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                value={registrationData.phone || ''}
                onChange={currentUser ? undefined : (e) => setRegistrationData({...registrationData, phone: e.target.value})}
                disabled={!!currentUser}
                className={`w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${currentUser ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed' : 'border-gray-300'}`}
                placeholder="Enter your phone number (optional)"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Dietary Restrictions
              </label>
              <textarea
                value={registrationData.dietaryRestrictions}
                onChange={(e) => setRegistrationData({...registrationData, dietaryRestrictions: e.target.value})}
                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                placeholder="Any dietary restrictions or allergies?"
                rows="2"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Special Requests
              </label>
              <textarea
                value={registrationData.specialRequests}
                onChange={(e) => setRegistrationData({...registrationData, specialRequests: e.target.value})}
                className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                placeholder="Any special requests or accommodations needed?"
                rows="2"
              />
            </div>

            {requiresPayment && (
              <div className="border-t pt-3 sm:pt-4">
                 <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3">
                   Payment Information
                 </label>
                {squareInitError ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-xs sm:text-sm text-red-800 break-words">{squareInitError}</p>
                  </div>
                ) : null}

                <div className="space-y-2.5 sm:space-y-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Card details</label>
                    <div className="px-2.5 sm:px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
                      <div id={containerId} />
                    </div>
                    {!squareReady && !squareInitError ? (
                      <p className="text-xs text-gray-500 mt-1">Loading payment form…</p>
                    ) : null}
                  </div>

                  {applePayAvailable ? (
                    <>
                      <div className="relative flex items-center gap-3 pt-1">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-xs text-gray-500 shrink-0">or</span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>
                      <div>
                        <button
                          type="button"
                          ref={applePayBtnRef}
                          className="bow-apple-pay-button"
                          disabled={paymentLoading}
                          aria-label="Pay with Apple Pay"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Apple Pay works in Safari with a card in Wallet. Register your domain under Square Developer →
                          Apple Pay for your production site (HTTPS).
                        </p>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            )}

            {validationErrors.general && (
              <div className="text-red-600 text-xs sm:text-sm break-words">{validationErrors.general}</div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-3 sm:pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 sm:py-2 px-4 text-sm sm:text-base border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                Cancel
              </button>
               <button
                 type="submit"
                 disabled={isRegistering || paymentLoading || (requiresPayment && !squareReady)}
                 className="flex-1 py-2.5 sm:py-2 px-4 text-sm sm:text-base bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors duration-200 break-words"
               >
                 {paymentLoading ? 'Processing Payment...' : isRegistering ? 'Registering...' : requiresPayment ? (() => {
                   let total = parseEventPrice(event.price) * registrationData.quantity;
                   if (selectedAddons && eventAddons) {
                     Object.keys(selectedAddons).forEach(addonId => {
                       const quantity = selectedAddons[addonId];
                       if (quantity > 0) {
                         const addon = eventAddons.find(a => a.id === addonId);
                         if (addon && !addon.isFreeWithTicket) {
                           total += addon.price * quantity;
                         }
                       }
                     });
                   }
                   return `Pay $${total.toFixed(2)} & Register ${registrationData.quantity} ${registrationData.quantity === 1 ? 'Person' : 'People'}`;
                 })() : 'Register'}
               </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Helper function to safely parse event price (handles both string "$10" and number 10)
const parseEventPrice = (price) => {
  if (price === null || price === undefined) return 0;
  if (typeof price === 'number') return price;
  if (typeof price === 'string') {
    // Handle "Free" string
    if (price.toLowerCase() === 'free') return 0;
    // Remove $ and any whitespace, then parse
    const cleaned = price.replace('$', '').trim();
    return parseFloat(cleaned) || 0;
  }
  return 0;
};

// Helper function to check if event is paid
const isPaidEvent = (price) => {
  const parsedPrice = parseEventPrice(price);
  return parsedPrice > 0;
};

// Helper function to check if there are any paid addons selected
const hasPaidAddons = (selectedAddons, eventAddons) => {
  if (!selectedAddons || !eventAddons) return false;
  return Object.keys(selectedAddons).some(addonId => {
    const quantity = selectedAddons[addonId];
    if (quantity > 0) {
      const addon = eventAddons.find(a => a.id === addonId);
      return addon && !addon.isFreeWithTicket && addon.price > 0;
    }
    return false;
  });
};

// Helper function to calculate total addon price
const calculateAddonTotal = (selectedAddons, eventAddons) => {
  if (!selectedAddons || !eventAddons) return 0;
  let total = 0;
  Object.keys(selectedAddons).forEach(addonId => {
    const quantity = selectedAddons[addonId];
    if (quantity > 0) {
      const addon = eventAddons.find(a => a.id === addonId);
      if (addon && !addon.isFreeWithTicket) {
        total += addon.price * quantity;
      }
    }
  });
  return total;
};

const EventDetailsPage = () => {
  const { id, idOrSlug } = useParams();
  const eventIdentifier = idOrSlug || id;
  const { currentUser } = useAuth();
  const { triggerConfetti } = useCelebration();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    name: currentUser ? (currentUser.displayName || '') : '',
    email: currentUser ? (currentUser.email || '') : '',
    phone: currentUser ? (currentUser.phone || '') : '',
    dietaryRestrictions: '',
    specialRequests: '',
    cardNumber: '',
    quantity: 1
  });
  const [isRegistering, setIsRegistering] = useState(false);
  const [ticketInfo, setTicketInfo] = useState(null);
  const [copiedTicket, setCopiedTicket] = useState(false);
  const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false);
  const [existingRegistration, setExistingRegistration] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [eventAddons, setEventAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState({}); // { addonId: quantity }
  const [registrationAddons, setRegistrationAddons] = useState([]); // Store addons for the current registration
  const [eventPhotos, setEventPhotos] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [activeScheduleTab, setActiveScheduleTab] = useState(0);
  const [showCalendarMenu, setShowCalendarMenu] = useState(false);

  // Fetch sponsors
  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await api.get('/sponsors');
        if (response.ok) {
          const data = await response.json();
          setSponsors(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Error fetching sponsors:', err);
      }
    };
    fetchSponsors();
  }, []);

  const handleWhatsAppShare = () => {
    if (!event) return;
    const targetSlug = event.slug || createEventSlug(event) || eventIdentifier;
    const cleanEventUrl = `https://beatsofwa.org/events/${targetSlug}`;
    const displayDate = formatEventDisplayDate(event);
    
    let message = event?.whatsappMessage;
    if (message) {
      if (!message.includes(cleanEventUrl)) {
        message = `${message}\n\n👉 Details & Registration:\n${cleanEventUrl}`;
      }
    } else {
      message = `🪔 *${event.title}* 🪔\n📅 *Date:* ${displayDate}\n📍 *Location:* ${event.location}\n\n🎉 Join Beats of Washington for this grand community celebration!\n\n👉 Click here for details & registration:\n${cleanEventUrl}`;
    }
    
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const generateGoogleCalendarUrl = (event) => {
    if (!event) return '#';
    const title = encodeURIComponent(event.title || 'BOW Event');
    const details = encodeURIComponent(event.description || event.longDescription || '');
    const location = encodeURIComponent(event.location || '');
    
    const startDate = event.date ? new Date(event.date) : new Date();
    const endDate = event.endDate ? new Date(event.endDate) : startDate;
    
    const startStr = startDate.toISOString().replace(/-|:|\.\d\d\d/g, '');
    const endStr = endDate.toISOString().replace(/-|:|\.\d\d\d/g, '');
    
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startStr}/${endStr}&details=${details}&location=${location}`;
  };

  const downloadIcsFile = (event) => {
    if (!event) return;
    const title = event.title || 'BOW Event';
    const description = event.description || '';
    const location = event.location || '';
    const startDate = (event.date ? new Date(event.date) : new Date()).toISOString().replace(/-|:|\.\d\d\d/g, '');
    const endDate = (event.endDate ? new Date(event.endDate) : new Date()).toISOString().replace(/-|:|\.\d\d\d/g, '');

    const icsData = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Beats of Washington//Event Calendar//EN',
      'BEGIN:VEVENT',
      `SUMMARY:${title}`,
      `DESCRIPTION:${description}`,
      `LOCATION:${location}`,
      `DTSTART:${startDate}`,
      `DTEND:${endDate}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([icsData], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `${title.replace(/\s+/g, '_')}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Calendar invite downloaded!');
  };

  // Fetch event gallery photos
  useEffect(() => {
    const fetchPhotos = async () => {
      if (!event?.id && !eventIdentifier) return;
      try {
        const response = await api.get('/gallery');
        if (response.ok) {
          const data = await response.json();
          const targetId = event?.id || eventIdentifier;
          const photos = data.filter(item => item.eventId === targetId || item.album === event?.title);
          setEventPhotos(photos);
        }
      } catch (err) {
        console.error('Error fetching event photos:', err);
      }
    };
    fetchPhotos();
  }, [event?.id, event?.title, eventIdentifier]);

  useEffect(() => {
    const fetchEvent = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/events/${eventIdentifier}`);
        if (!response.ok) throw new Error('Event not found');
        const data = await response.json();
        setEvent(data);

        // Update URL bar seamlessly if accessed via old ID
        const targetSlug = data.slug || createEventSlug(data);
        if (typeof window !== 'undefined' && targetSlug && eventIdentifier !== targetSlug) {
          window.history.replaceState(null, '', `/events/${targetSlug}`);
        }
      } catch (err) {
        setEvent(null);
      } finally {
        setLoading(false);
      }
    };
    if (eventIdentifier) {
      fetchEvent();
    }
  }, [eventIdentifier]);

  // Fetch event addons
  useEffect(() => {
    const fetchAddons = async () => {
      if (!eventIdentifier) return;
      try {
        const response = await api.get(`/events/${eventIdentifier}/addons`);
        if (response.ok) {
          const data = await response.json();
          setEventAddons(data || []);
        } else {
          setEventAddons([]);
        }
      } catch (err) {
        console.error('Error fetching addons:', err);
        setEventAddons([]);
      }
    };
    fetchAddons();
  }, [eventIdentifier]);

  // Fetch logo from about page content
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await api.get('/about-page');
        if (response.ok) {
          const data = await response.json();
          if (data.logo) {
            setLogoUrl(data.logo);
          }
        }
      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    };
    
    fetchLogo();
  }, []);

  // Keep registrationData in sync with currentUser
  useEffect(() => {
    if (currentUser) {
      console.log('Current user data:', currentUser);
      const userName = currentUser.displayName || 
                      (currentUser.firstName && currentUser.lastName ? `${currentUser.firstName} ${currentUser.lastName}`.trim() : null) ||
                      currentUser.firstName ||
                      currentUser.email || '';
      console.log('Setting userName to:', userName);
      setRegistrationData(data => ({
        ...data,
        name: userName,
        email: currentUser.email || '',
        phone: currentUser.phone || ''
      }));
    }
  }, [currentUser]);

  // Check if user is already registered for this event
  useEffect(() => {
    const checkExistingRegistration = async () => {
      if (!event) return;
      
      if (currentUser) {
        // For logged-in users, check from backend
        try {
          const userId = currentUser.uid || currentUser.id;
          const response = await api.get(`/events/user/${userId}/registrations`);
          if (response.ok) {
            const registrations = await response.json();
            const existingReg = registrations.find(reg => reg.eventId === event.id || reg.eventId === event._id);
            
            if (existingReg) {
              setIsAlreadyRegistered(true);
              setExistingRegistration(existingReg);
              setTicketInfo({
                ticketNumber: existingReg.ticketNumber,
                registration: existingReg
              });
            }
          }
        } catch (error) {
          console.error('Error checking existing registration:', error);
        }
      } else {
        // For guest users, check localStorage
        const guestRegistrations = JSON.parse(localStorage.getItem('guestRegistrations') || '[]');
        const existingReg = guestRegistrations.find(reg => reg.eventId === event.id || reg.eventId === event._id);
        
        if (existingReg) {
          setIsAlreadyRegistered(true);
          setExistingRegistration(existingReg);
          setTicketInfo({
            ticketNumber: existingReg.ticketNumber,
            registration: existingReg
          });
        }
      }
    };
    
    checkExistingRegistration();
  }, [currentUser, event]);

  // Add function to handle registration button click via Zeffy (opens direct for instant speed & Apple Pay)
  const handleRegistrationClick = () => {
    if (event && isEventDatePast(event.date)) {
      toast.error('Registration is closed — this event has already taken place.');
      return;
    }
    const zeffyUrl = event?.zeffyUrl || event?.registrationUrl || event?.externalRegistrationUrl || 'https://www.zeffy.com/en-US/ticketing/washington-ganesh-festival--2026';
    window.open(zeffyUrl, '_blank', 'noopener,noreferrer');
  };

  const handleRegistration = async (paymentIntentId = null) => {
    setIsRegistering(true);
    try {
      // Extra validation for required fields
      if (!currentUser && (!registrationData.name || !registrationData.email)) {
        toast.error('Name and email are required.');
        setIsRegistering(false);
        return;
      }
      
      // Prepare request body
      let requestBody;
      let headers = { 'Content-Type': 'application/json' };
      
       // Calculate payment amount if it's a paid event
       let paymentAmount = 0;
       const eventPrice = parseEventPrice(event.price);
       if (eventPrice > 0) {
         paymentAmount = eventPrice * 100 * registrationData.quantity; // Convert to cents and multiply by quantity
       }
       
       // Add addon prices (excluding free items)
       const addonsArray = [];
       if (selectedAddons && eventAddons) {
         Object.keys(selectedAddons).forEach(addonId => {
           const quantity = selectedAddons[addonId];
           if (quantity > 0) {
             const addon = eventAddons.find(a => a.id === addonId);
             if (addon && !addon.isFreeWithTicket) {
               paymentAmount += (addon.price * 100 * quantity); // Convert to cents
               addonsArray.push({ addonId, quantity });
             } else if (addon && addon.isFreeWithTicket) {
               // Include free addons in the array for tracking
               addonsArray.push({ addonId, quantity });
             }
           }
         });
       }
      
      if (currentUser) {
        requestBody = {
          userId: currentUser.uid || currentUser.id,
          userEmail: currentUser.email,
          userName: currentUser.displayName || 
                   (currentUser.firstName && currentUser.lastName ? `${currentUser.firstName} ${currentUser.lastName}`.trim() : null) ||
                   currentUser.firstName ||
                   currentUser.email,
          phone: currentUser.phone || registrationData.phone || '',
          dietaryRestrictions: registrationData.dietaryRestrictions,
          specialRequests: registrationData.specialRequests,
          quantity: registrationData.quantity,
          paymentAmount: paymentAmount,
          paymentIntentId: paymentIntentId,
          isPaidEvent: paymentAmount > 0,
          addons: addonsArray
        };
        console.log('Registration data for logged-in user:', requestBody);
        // Add auth token if available
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
          headers['Authorization'] = `Bearer ${authToken}`;
        }
      } else {
        const tempUserId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        requestBody = {
          userId: tempUserId,
          userEmail: registrationData.email,
          userName: registrationData.name,
          phone: registrationData.phone || '',
          dietaryRestrictions: registrationData.dietaryRestrictions,
          specialRequests: registrationData.specialRequests,
          quantity: registrationData.quantity,
          paymentAmount: paymentAmount,
          paymentIntentId: paymentIntentId,
          isPaidEvent: paymentAmount > 0,
          addons: addonsArray
        };
        const eventTargetId = event?.id || eventIdentifier;
        console.log('Registration data for guest user:', requestBody);
      }
      const eventTargetId = event?.id || eventIdentifier;
      const response = await api.post(`/events/${eventTargetId}/register`, requestBody, { headers });
      if (response.ok) {
        const result = await response.json();
        // Store addon details for display in confirmation
        const addonDetails = [];
        if (selectedAddons && eventAddons) {
          Object.keys(selectedAddons).forEach(addonId => {
            const quantity = selectedAddons[addonId];
            if (quantity > 0) {
              const addon = eventAddons.find(a => a.id === addonId);
              if (addon) {
                addonDetails.push({
                  id: addon.id,
                  name: addon.name,
                  price: addon.price,
                  quantity: quantity,
                  isFreeWithTicket: addon.isFreeWithTicket
                });
              }
            }
          });
        }
        // Also check if registration has addon info stored
        if (result.registration && result.registration.addons && result.registration.addons.length > 0) {
          // Fetch addon details from stored addon IDs
          const storedAddonDetails = [];
          for (const addonItem of result.registration.addons) {
            const addon = eventAddons.find(a => a.id === addonItem.addonId);
            if (addon) {
              storedAddonDetails.push({
                id: addon.id,
                name: addon.name,
                price: addon.price,
                quantity: addonItem.quantity || 1,
                isFreeWithTicket: addon.isFreeWithTicket
              });
            }
          }
          if (storedAddonDetails.length > 0) {
            setRegistrationAddons(storedAddonDetails);
          } else {
            setRegistrationAddons(addonDetails);
          }
        } else {
          setRegistrationAddons(addonDetails);
        }
        setTicketInfo(result);
        setShowRegistrationModal(false);
        setIsAlreadyRegistered(true);
        setExistingRegistration(result.registration);
        
        // Save guest registration to localStorage if not logged in
        if (!currentUser && result.registration) {
          const guestRegistrations = JSON.parse(localStorage.getItem('guestRegistrations') || '[]');
          guestRegistrations.push(result.registration);
          localStorage.setItem('guestRegistrations', JSON.stringify(guestRegistrations));
        }
        
        toast.success('Registration successful! Check your email for ticket details.');
        triggerConfetti();
        
        // Refetch the event to get the updated registration count
        console.log('[Frontend] Refetching event data after registration...');
        const eventResponse = await api.get(`/events/${eventTargetId}`);
        if (eventResponse.ok) {
          const updatedEvent = await eventResponse.json();
          console.log('[Frontend] Updated event data:', updatedEvent);
          setEvent(updatedEvent);
        } else {
          console.log('[Frontend] Failed to refetch event, using fallback increment');
          // Fallback to manual increment if refetch fails
          setEvent(prev => ({ ...prev, registeredCount: prev.registeredCount + 1 }));
        }
      } else {
        const errorData = await response.json();
        
        // Check if user is already registered
        if (errorData.message && errorData.message.includes('already registered')) {
          setIsAlreadyRegistered(true);
          if (errorData.registration) {
            setExistingRegistration(errorData.registration);
            setTicketInfo({
              ticketNumber: errorData.registration.ticketNumber,
              registration: errorData.registration
            });
          }
          toast.info('You are already registered for this event!');
        } else {
          toast.error(errorData.message || 'Registration failed');
        }
      }
    } catch (error) {
      console.error('[Registration] Network or other error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setIsRegistering(false);
    }
  };

    if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <>
        <Helmet>
          <title>Event Not Found | Beats of Washington</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Event Not Found</h1>
            <p className="text-gray-600 mb-8">The event you're looking for doesn't exist.</p>
            <Link to="/events" className="btn-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Events
            </Link>
          </div>
        </div>
      </>
    );
  }

  const registrationPercentage = (event.registeredCount / event.capacity) * 100;
  const isEventPast = isEventDatePast(event);
  const isRegistrationOpen =
    event.isLive && event.isActive && event.registeredCount < event.capacity && !isEventPast;
  
  // Calculate if payment is required (paid event OR paid addons)
  const isPaidEventValue = event ? isPaidEvent(event.price) : false;
  const hasPaidAddonsValue = hasPaidAddons(selectedAddons, eventAddons);
  const requiresPayment = isPaidEventValue || hasPaidAddonsValue;

  const city = extractCity(event?.location, event?.address);
  const year = extractYear(event?.date);
  const eventSlug = event ? (event.slug || createEventSlug(event)) : '';
  const baseUrl = getProductionBaseUrl();
  const canonicalUrl = `${baseUrl}/events/${eventSlug}`;
  const displayCity = city ? (city.charAt(0).toUpperCase() + city.slice(1)) : 'Seattle';
  
  // Smart SEO Title targeting both event location and Seattle search queries
  const titleLower = (event?.title || '').toLowerCase();
  const hasSeattle = titleLower.includes('seattle');
  const hasDisplayCity = titleLower.includes(displayCity.toLowerCase());
  
  let cityKeyword = '';
  if (!hasSeattle && !hasDisplayCity) {
    cityKeyword = `${displayCity} Seattle`;
  } else if (!hasSeattle) {
    cityKeyword = 'Seattle';
  } else if (!hasDisplayCity && displayCity.toLowerCase() !== 'seattle') {
    cityKeyword = displayCity;
  }

  const seoTitle = `${event?.title || 'Cultural Event'} ${cityKeyword} ${year} | Beats of Washington`
    .replace(/\s+/g, ' ')
    .trim();
  
  // Dynamic Meta Description targeting Seattle, Redmond, Bellevue & WA regional search queries
  const metaDescription = event?.description 
    ? `${event.description.slice(0, 140)}... Find dates, venue location, schedule & registration details for ${event.title} in Seattle, Redmond & Bellevue WA on Beats of Washington.`
    : `Celebrate ${event?.title || 'Event'} in Seattle, Redmond & Bellevue WA (${year}). Find dates, venue location, schedule, cultural performances & free registration details on Beats of Washington.`;

  // Smart date formatter: e.g. "Sep 18-19 & 20, 2026"
  const formatEventDisplayDate = (evt) => {
    if (!evt) return '';
    if (evt.displayDate) return evt.displayDate;
    
    const startStr = evt.date;
    const endStr = evt.endDate;
    
    if (!startStr) return '';
    
    if (!startStr.match(/^\d{4}-\d{2}-\d{2}/)) {
      return startStr;
    }
    
    const startObj = parseDateString(startStr);
    const endObj = endStr ? parseDateString(endStr) : null;
    
    if (!startObj) return startStr;
    
    const monthName = startObj.toLocaleDateString('en-US', { month: 'short' });
    const yearNum = startObj.getFullYear();
    
    if (endObj && startObj.getMonth() === endObj.getMonth() && startObj.getFullYear() === endObj.getFullYear()) {
      const startDay = startObj.getDate();
      const endDay = endObj.getDate();
      
      if (endDay - startDay === 2) {
        return `${monthName} ${startDay}-${startDay + 1} & ${endDay}, ${yearNum}`;
      } else if (endDay > startDay) {
        return `${monthName} ${startDay}-${endDay}, ${yearNum}`;
      }
    }
    
    return formatEventDateRange(startStr, endStr, 'short');
  };

  // ISO 8601 Date helper for Schema.org
  const getIsoDate = (dateStr) => {
    if (!dateStr) return new Date().toISOString();
    const parsed = new Date(dateStr);
    return !isNaN(parsed.getTime()) ? parsed.toISOString() : new Date().toISOString();
  };

  const startDateIso = getIsoDate(event.date);
  const endDateIso = event.endDate ? getIsoDate(event.endDate) : startDateIso;

  const getAbsoluteImageUrl = (imgUrl) => {
    if (!imgUrl) return 'https://beatsofwa.org/logo.png';
    if (imgUrl.startsWith('http://') || imgUrl.startsWith('https://')) return imgUrl;
    if (imgUrl.startsWith('/')) return `${baseUrl}${imgUrl}`;
    return `${baseUrl}/${imgUrl}`;
  };

  const socialImageUrl = getAbsoluteImageUrl(event?.image);

  const eventSchema = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": event.title,
    "description": event.longDescription || event.description || seoTitle,
    "startDate": startDateIso,
    "endDate": endDateIso,
    "eventStatus": "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "image": [socialImageUrl],
    "url": canonicalUrl,
    "location": {
      "@type": "Place",
      "name": event.location || "Seattle Venue",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": event.address || event.location || "Seattle",
        "addressLocality": displayCity,
        "addressRegion": "WA",
        "addressCountry": "US"
      }
    },
    "organizer": {
      "@type": "Organization",
      "name": event.organizer || "Beats of Washington",
      "url": baseUrl
    },
    "offers": {
      "@type": "Offer",
      "price": event.price ? parseEventPrice(event.price).toString() : "0",
      "priceCurrency": "USD",
      "availability": event.capacity && event.registeredCount >= event.capacity ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
      "url": canonicalUrl,
      "validFrom": event.createdAt || new Date().toISOString()
    }
  };

  const copyTicketToClipboard = async () => {
    if (ticketInfo?.ticketNumber) {
      try {
        await navigator.clipboard.writeText(ticketInfo.ticketNumber);
        setCopiedTicket(true);
        toast.success('Ticket number copied to clipboard!');
        setTimeout(() => setCopiedTicket(false), 2000);
      } catch (err) {
        toast.error('Failed to copy ticket number');
      }
    }
  };

  const generateReceipt = () => {
    if (!ticketInfo?.registration) return;
    
    const registration = ticketInfo.registration;
    const receiptData = {
      event: event.title,
      date: event.date,
      time: event.time,
      location: event.location,
      ticketNumber: ticketInfo.ticketNumber,
      attendeeName: registration.userName,
      attendeeEmail: registration.userEmail,
      paymentAmount: (registration.paymentAmount / 100).toFixed(2),
      paymentDate: new Date(registration.paymentDate).toLocaleDateString(),
      transactionId: registration.paymentIntentId,
      receiptDate: new Date().toLocaleDateString()
    };

    // Create receipt HTML
    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Event Registration Receipt</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { text-align: center; border-bottom: 2px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; }
          .receipt-title { color: #2563eb; font-size: 28px; font-weight: bold; margin: 0; }
          .receipt-subtitle { color: #6b7280; margin: 5px 0 0 0; }
          .section { margin-bottom: 25px; }
          .section-title { font-weight: bold; color: #374151; margin-bottom: 10px; font-size: 16px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 8px; }
          .info-label { color: #6b7280; }
          .info-value { font-weight: 500; color: #111827; }
          .payment-section { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-top: 20px; }
          .total { font-size: 18px; font-weight: bold; color: #059669; }
          .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 14px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1 class="receipt-title">Beats of Washington</h1>
          <p class="receipt-subtitle">Event Registration Receipt</p>
        </div>
        
        <div class="section">
          <div class="section-title">Event Details</div>
          <div class="info-row">
            <span class="info-label">Event:</span>
            <span class="info-value">${receiptData.event}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Date:</span>
            <span class="info-value">${receiptData.date}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Time:</span>
            <span class="info-value">${receiptData.time}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Location:</span>
            <span class="info-value">${receiptData.location}</span>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Registration Details</div>
          <div class="info-row">
            <span class="info-label">Ticket Number:</span>
            <span class="info-value">${receiptData.ticketNumber}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Attendee Name:</span>
            <span class="info-value">${receiptData.attendeeName}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Email:</span>
            <span class="info-value">${receiptData.attendeeEmail}</span>
          </div>
        </div>
        
        <div class="payment-section">
          <div class="section-title">Payment Information</div>
          <div class="info-row">
            <span class="info-label">Amount Paid:</span>
            <span class="info-value total">$${receiptData.paymentAmount}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Payment Date:</span>
            <span class="info-value">${receiptData.paymentDate}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Transaction ID:</span>
            <span class="info-value">${receiptData.transactionId}</span>
          </div>
        </div>
        
        <div class="footer">
          <p>Thank you for supporting Beats of Washington!</p>
          <p>Receipt generated on ${receiptData.receiptDate}</p>
        </div>
      </body>
      </html>
    `;

    // Create and download the receipt
    const blob = new Blob([receiptHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt-${receiptData.ticketNumber}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Receipt downloaded successfully!');
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook / WhatsApp */}
        <meta property="og:type" content="event" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={socialImageUrl} />
        <meta property="og:image:secure_url" content={socialImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Beats of Washington" />

        {/* Twitter / X */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={socialImageUrl} />

        {/* Google Event Structured Data JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(eventSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 overflow-x-hidden max-w-full">
        {/* Hero Section Container with Stage Lighting & Desktop Beautification */}
        <div className="relative min-h-[340px] sm:min-h-[420px] md:min-h-[500px] lg:min-h-[560px] overflow-hidden bg-gradient-to-br from-gray-950 via-slate-950 to-gray-900 shadow-2xl flex items-center justify-center p-3 sm:p-6 md:p-8">
          
          {/* Ambient Blurred Artwork Background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
            <img 
              src={event.image} 
              alt="" 
              className="w-full h-full object-cover blur-3xl transform scale-150 filter saturate-200" 
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/40 to-black/90" />
          </div>

          {/* Decorative Glowing Ambient Spotlights for Desktop Beautification */}
          <div className="absolute left-4 top-1/4 w-72 h-72 bg-orange-600/20 rounded-full blur-[100px] pointer-events-none hidden md:block" />
          <div className="absolute right-4 bottom-1/4 w-72 h-72 bg-amber-500/20 rounded-full blur-[100px] pointer-events-none hidden md:block" />

          {/* Symmetrical Floating Diya 🪔 and Star ✨ on Both Sides Equally */}
          <div className="absolute left-6 lg:left-14 top-1/2 -translate-y-1/2 z-20 pointer-events-none hidden md:flex flex-col items-center gap-8">
            <span className="text-3xl lg:text-4xl filter drop-shadow-[0_0_15px_rgba(249,115,22,0.9)] transform -rotate-12 animate-pulse">🪔</span>
            <span className="text-2xl lg:text-3xl filter drop-shadow-[0_0_12px_rgba(245,158,11,0.9)] transform rotate-12 animate-pulse">✨</span>
            <span className="text-3xl lg:text-4xl filter drop-shadow-[0_0_15px_rgba(249,115,22,0.9)] transform rotate-12 animate-pulse">🪔</span>
          </div>

          <div className="absolute right-6 lg:right-14 top-1/2 -translate-y-1/2 z-20 pointer-events-none hidden md:flex flex-col items-center gap-8">
            <span className="text-3xl lg:text-4xl filter drop-shadow-[0_0_15px_rgba(249,115,22,0.9)] transform rotate-12 animate-pulse">🪔</span>
            <span className="text-2xl lg:text-3xl filter drop-shadow-[0_0_12px_rgba(245,158,11,0.9)] transform -rotate-12 animate-pulse">✨</span>
            <span className="text-3xl lg:text-4xl filter drop-shadow-[0_0_15px_rgba(249,115,22,0.9)] transform -rotate-12 animate-pulse">🪔</span>
          </div>

          {/* Main Event Poster Card - Curved Edges (rounded-2xl sm:rounded-3xl) with Floating Glow */}
          <div className="relative z-10 max-w-full flex items-center justify-center py-2 px-1 sm:px-4">
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border border-white/20 ring-1 ring-white/10 group transition-all duration-500 hover:shadow-orange-500/10">
              <img
                src={event.image}
                alt={`${event.title} ${displayCity} ${year} - Beats of Washington`}
                className="w-auto h-auto max-h-[300px] sm:max-h-[380px] md:max-h-[460px] lg:max-h-[500px] object-contain rounded-2xl sm:rounded-3xl transition-transform duration-500 group-hover:scale-[1.01]"
                loading="eager"
              />
            </div>
          </div>
          
          {/* Bottom Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent z-20 pointer-events-none"></div>
          
          <div className="absolute inset-0 z-30 h-full flex flex-col justify-between p-4 sm:p-6 md:p-8 min-h-[340px] sm:min-h-[420px] md:min-h-[500px] lg:min-h-[560px]">
            {/* Top Bar: Back to Events button */}
            <div>
              <Link to="/events" className="inline-flex items-center text-white/95 hover:text-white bg-black/60 hover:bg-black/80 border border-white/15 px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium backdrop-blur-md transition-all shadow-lg hover:scale-105">
                <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                Back to Events
              </Link>
            </div>

            {/* Bottom Bar: Date, Time & Location positioned at bottom of pic */}
            <div className="container-custom px-0 mt-auto">
              <div className="max-w-4xl">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:gap-4 text-white/95 text-xs sm:text-sm md:text-base font-medium">
                  <div className="flex items-center bg-black/50 hover:bg-black/70 px-3 py-1.5 rounded-xl backdrop-blur-md border border-white/15 shadow-md">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-orange-400 flex-shrink-0" />
                    <span className="break-words">{formatEventDisplayDate(event)}</span>
                  </div>
                  <div className="flex items-center bg-black/50 hover:bg-black/70 px-3 py-1.5 rounded-xl backdrop-blur-md border border-white/15 shadow-md">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-orange-400 flex-shrink-0" />
                    <span className="break-words">{event.time}</span>
                  </div>
                  <div className="flex items-center bg-black/50 hover:bg-black/70 px-3 py-1.5 rounded-xl backdrop-blur-md border border-white/15 shadow-md">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-orange-400 flex-shrink-0" />
                    <span className="break-words">{event.location}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container-custom py-8 sm:py-10 md:py-12 px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6 sm:space-y-8">
              {/* Event Details Section - Showing Event Title from Admin Panel */}
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 md:p-8 border-l-4 border-primary-500 hover:shadow-xl transition-all duration-300">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <div className="flex items-center min-w-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center mr-3 sm:mr-4 overflow-hidden flex-shrink-0">
                      {logoUrl ? (
                        <img 
                          src={logoUrl} 
                          alt="BOW Logo" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Star className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      )}
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{event.title}</h2>
                  </div>
                  <div className="flex space-x-1.5 sm:space-x-2 flex-shrink-0">
                    <button className="p-1.5 sm:p-2 text-gray-600 hover:text-primary-600 transition-colors hover:bg-primary-50 rounded-lg" aria-label="Share event">
                      <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    <button className="p-1.5 sm:p-2 text-gray-600 hover:text-red-600 transition-colors hover:bg-red-50 rounded-lg" aria-label="Like event">
                      <Heart className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </div>

                <div className="prose max-w-none">
                  <div className="whitespace-pre-line text-sm sm:text-base text-gray-700 leading-relaxed bg-gray-50 p-4 sm:p-5 md:p-6 rounded-lg border-l-4 border-primary-200 break-words">
                    {event.longDescription || event.description}
                  </div>
                </div>
              </div>

              {/* Multi-Day Event Schedule & Agenda Timeline - Only rendered if custom schedule is set by admin */}
              {Array.isArray(event?.schedule) && event.schedule.length > 0 && (() => {
                const scheduleData = event.schedule;

                return (
                  <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 md:p-8 border-l-4 border-amber-500 hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center justify-between mb-4 sm:mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center text-white font-bold text-lg shadow-md">
                          🗓️
                        </div>
                        <div>
                          <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Festival Schedule & Agenda</h3>
                          <p className="text-xs sm:text-sm text-gray-500">Day-by-day program schedule & performance highlights</p>
                        </div>
                      </div>
                    </div>

                    {/* Day Tabs */}
                    <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto pb-2">
                      {scheduleData.map((day, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setActiveScheduleTab(idx)}
                          className={`px-4 py-2 rounded-xl font-bold text-xs sm:text-sm transition-all whitespace-nowrap ${
                            activeScheduleTab === idx
                              ? 'bg-amber-500 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {day.dayTitle}
                        </button>
                      ))}
                    </div>

                    {/* Timeline List */}
                    <div className="space-y-3.5 max-w-full overflow-hidden">
                      {scheduleData[activeScheduleTab]?.events?.map((item, i) => (
                        <div key={i} className="flex flex-col sm:flex-row gap-2.5 sm:gap-4 p-3 sm:p-4 rounded-xl bg-orange-50/50 border border-orange-100/80 hover:bg-orange-50 transition-colors max-w-full overflow-hidden">
                          <div className="flex flex-row sm:flex-col items-center justify-center px-3 py-1.5 bg-orange-500/10 rounded-lg border border-orange-200 w-full sm:w-auto sm:min-w-[125px] text-center flex-shrink-0">
                            <Clock className="w-3.5 h-3.5 text-orange-600 mr-1.5 sm:mr-0 sm:mb-0.5" />
                            <span className="text-xs sm:text-sm font-bold text-orange-900">{item.time}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm sm:text-base font-bold text-gray-900 flex items-center gap-2 flex-wrap">
                              <span>{item.icon || '🥁'}</span>
                              <span className="break-words">{item.title}</span>
                            </h4>
                            {item.description && <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">{item.description}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Tags Section */}
              <div className="hidden sm:block bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 md:p-8 border-l-4 border-secondary-500 hover:shadow-xl transition-all duration-300">
                <div className="flex items-center mb-3 sm:mb-4">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                    <span className="text-white text-xs sm:text-sm font-bold">#</span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 break-words">Event Tags</h3>
                </div>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {event.tags && event.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 bg-secondary-100 text-secondary-800 rounded-full text-xs sm:text-sm font-medium hover:bg-secondary-200 transition-colors duration-200 cursor-default break-words"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-4 sm:space-y-6">
              {/* Event Addons Section */}
              {eventAddons.length > 0 && (
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-5 md:p-6 border-l-4 border-purple-500 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center mb-3 sm:mb-4">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                      <ShoppingCart className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 break-words">Available Items</h3>
                  </div>
                  
                  <div className="space-y-3 sm:space-y-4">
                    {eventAddons.map((addon) => {
                      const isOutOfStock = addon.stock !== null && addon.availableStock !== null && addon.availableStock <= 0;
                      const quantity = selectedAddons[addon.id] || 0;
                      const isFreeWithTicket = addon.isFreeWithTicket && addon.freeQuantityPerTicket > 0;
                      
                      return (
                        <div
                          key={addon.id}
                          className={`p-3 sm:p-4 rounded-lg border ${
                            isOutOfStock ? 'bg-gray-100 border-gray-300 opacity-60' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          } transition-colors duration-200`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="text-sm sm:text-base font-semibold text-gray-900 break-words">{addon.name}</h4>
                                {isFreeWithTicket ? (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold">
                                    FREE {addon.freeQuantityPerTicket} per ticket
                                  </span>
                                ) : (
                                  <span className="text-sm sm:text-base font-bold text-purple-600">
                                    ${addon.price.toFixed(2)}
                                  </span>
                                )}
                                {addon.stock !== null && addon.availableStock !== null && (
                                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                    addon.availableStock > 0 
                                      ? 'bg-yellow-100 text-yellow-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {addon.availableStock > 0 ? `${addon.availableStock} left!` : 'SOLD OUT'}
                                  </span>
                                )}
                              </div>
                              {addon.description && (
                                <p className="text-xs sm:text-sm text-gray-600 mb-2 break-words">{addon.description}</p>
                              )}
                            </div>
                          </div>
                          
                          {!isFreeWithTicket && !isOutOfStock && (
                            <div className="flex items-center gap-2 mt-2">
                              <button
                                onClick={() => {
                                  if (quantity > 0) {
                                    setSelectedAddons({ ...selectedAddons, [addon.id]: quantity - 1 });
                                  }
                                }}
                                disabled={quantity === 0}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                                  quantity === 0 
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                                }`}
                              >
                                −
                              </button>
                              <span className="text-sm sm:text-base font-semibold text-gray-900 min-w-[2rem] text-center">
                                {quantity}
                              </span>
                              <button
                                onClick={() => {
                                  const maxQty = addon.stock !== null && addon.availableStock !== null 
                                    ? addon.availableStock 
                                    : 999;
                                  if (quantity < maxQty) {
                                    setSelectedAddons({ ...selectedAddons, [addon.id]: quantity + 1 });
                                  }
                                }}
                                disabled={addon.stock !== null && addon.availableStock !== null && quantity >= addon.availableStock}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                                  (addon.stock !== null && addon.availableStock !== null && quantity >= addon.availableStock)
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                                    : 'bg-purple-600 text-white hover:bg-purple-700'
                                }`}
                              >
                                +
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Total Addon Price */}
                    {Object.keys(selectedAddons).some(id => selectedAddons[id] > 0) && (
                      <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-purple-900">Addon Total:</span>
                          <span className="text-base font-bold text-purple-700">
                            ${Object.keys(selectedAddons).reduce((total, addonId) => {
                              const addon = eventAddons.find(a => a.id === addonId);
                              if (!addon || addon.isFreeWithTicket) return total;
                              return total + (addon.price * selectedAddons[addonId]);
                            }, 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Registration Section */}
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-5 md:p-6 border-l-4 border-green-500 hover:shadow-xl transition-all duration-300">
                <div className="flex items-center mb-3 sm:mb-4">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center mr-2 sm:mr-3 overflow-hidden flex-shrink-0">
                    {event.image ? (
                      <img
                        src={event.image}
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                        <Ticket className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 break-words">Registration</h3>
                </div>
                
                <div className="space-y-3 sm:space-y-4">
                  {/* Trust Feature Badges */}
                  <div className="space-y-2.5 py-1 px-1">
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 font-medium">
                      <span className="text-base flex-shrink-0">⚡</span>
                      <span>Instant Digital E-Ticket Confirmation</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 font-medium">
                      <span className="text-base flex-shrink-0">❤️</span>
                      <span>Non-Profit Community Event</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 font-medium">
                      <span className="text-base flex-shrink-0">🛡️</span>
                      <span>Zero Processing Surcharges</span>
                    </div>
                  </div>
                  
                  {isAlreadyRegistered ? (
                    <div className="text-center py-3 sm:py-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                        <div className="flex items-center justify-center mb-2">
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-1.5 sm:mr-2 flex-shrink-0" />
                          <span className="text-sm sm:text-base text-green-800 font-medium break-words">Already Registered</span>
                        </div>
                        <p className="text-xs sm:text-sm text-green-700 break-words">You have successfully registered for this event!</p>
                        {ticketInfo?.ticketNumber && (
                          <div className="mt-2 sm:mt-3 p-2 sm:p-3 bg-white rounded border">
                            <p className="text-xs sm:text-sm text-gray-600 mb-1">Your Ticket Number:</p>
                            <p className="font-mono text-xs sm:text-sm text-green-700 break-all">{ticketInfo.ticketNumber}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : isEventPast ? (
                    <div className="text-center py-3 sm:py-4">
                      <div className="bg-slate-100 border border-slate-200 rounded-lg p-3 sm:p-4">
                        <div className="flex items-center justify-center mb-2">
                          <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600 mr-1.5 sm:mr-2 flex-shrink-0" />
                          <span className="text-sm sm:text-base text-slate-800 font-medium break-words">Registration Closed</span>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-600 break-words">
                          This event has already taken place. Registration is no longer available.
                        </p>
                      </div>
                    </div>
                  ) : event.isLive ? (
                    <button 
                      onClick={handleRegistrationClick}
                      className={`w-full py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg text-sm sm:text-base font-semibold transition-all duration-200 transform hover:scale-105 ${
                        isRegistrationOpen
                          ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg' 
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                      disabled={!isRegistrationOpen}
                    >
                      {!isRegistrationOpen 
                        ? 'Registration Full' 
                        : 'Register Now'
                      }
                    </button>
                  ) : (
                    <div className="text-center py-3 sm:py-4">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                        <div className="flex items-center justify-center mb-2">
                          <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 mr-1.5 sm:mr-2 flex-shrink-0" />
                          <span className="text-sm sm:text-base text-yellow-800 font-medium break-words">Registration Not Open</span>
                        </div>
                        <p className="text-xs sm:text-sm text-yellow-700 break-words">This event is not yet live for registration. Check back later!</p>
                      </div>
                    </div>
                  )}

                  {/* Always Visible Donate Button in Registration Section */}
                  <div className="pt-2 border-t border-gray-100 space-y-2.5">
                    <a
                      href={event?.donationUrl || event?.zeffyDonationUrl || "https://www.zeffy.com/en-US/donation-form/548a0956-2611-4a95-ac05-1178941e5f09"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg text-sm sm:text-base font-bold transition-all duration-200 flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 via-red-500 to-amber-500 hover:from-orange-600 hover:via-red-600 hover:to-amber-600 text-white shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                    >
                      <Heart className="w-4 h-4 sm:w-5 sm:h-5 fill-current text-white animate-pulse" />
                      <span>Donate to Support BOW</span>
                    </a>

                    {/* WhatsApp Community Share Button */}
                    <button
                      type="button"
                      onClick={handleWhatsAppShare}
                      className="w-full py-2.5 px-4 rounded-lg text-sm font-bold text-white bg-[#25D366] hover:bg-[#128C7E] transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                    >
                      <MessageCircle className="w-4 h-4 fill-current" />
                      <span>Share on WhatsApp</span>
                    </button>

                    {/* Add to Calendar Button & Dropdown */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowCalendarMenu(v => !v)}
                        className="w-full py-2.5 px-4 rounded-lg text-sm font-bold text-gray-800 bg-gray-100 hover:bg-gray-200 border border-gray-300 transition-all duration-200 flex items-center justify-center gap-2 shadow-sm"
                      >
                        <Calendar className="w-4 h-4 text-orange-600" />
                        <span>📅 Add to Calendar</span>
                      </button>

                      {showCalendarMenu && (
                        <div className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 p-2 space-y-1 animate-fade-in">
                          <a
                            href={generateGoogleCalendarUrl(event)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setShowCalendarMenu(false)}
                            className="flex items-center gap-2.5 px-3 py-2 text-xs sm:text-sm font-semibold text-gray-800 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors"
                          >
                            <span>🌐 Google Calendar</span>
                          </a>
                          <button
                            type="button"
                            onClick={() => {
                              downloadIcsFile(event);
                              setShowCalendarMenu(false);
                            }}
                            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs sm:text-sm font-semibold text-gray-800 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors text-left"
                          >
                            <span>🍏 Apple Calendar (iCal)</span>
                          </button>
                          <a
                            href={generateGoogleCalendarUrl(event)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setShowCalendarMenu(false)}
                            className="flex items-center gap-2.5 px-3 py-2 text-xs sm:text-sm font-semibold text-gray-800 hover:bg-orange-50 hover:text-orange-600 rounded-lg transition-colors"
                          >
                            <span>📬 Outlook / Web Calendar</span>
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Event Information Section */}
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-5 md:p-6 border-l-4 border-blue-500 hover:shadow-xl transition-all duration-300">
                <div className="flex items-center mb-3 sm:mb-4">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                    <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 break-words">Event Information</h3>
                </div>
                
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start p-2.5 sm:p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-200">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 mt-0.5 text-blue-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm sm:text-base font-medium text-gray-900 break-words">
                        {formatEventDisplayDate(event)}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600 break-words">{event.time}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start p-2.5 sm:p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-200">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 mt-0.5 text-blue-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm sm:text-base font-medium text-gray-900 break-words">{event.location}</p>
                      <p className="text-xs sm:text-sm text-gray-600 break-words">{event.address || event.location}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center p-2.5 sm:p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-200">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 text-blue-500 flex-shrink-0" />
                    <span className="text-sm sm:text-base text-gray-900 break-words">{event.organizer}</span>
                  </div>
                </div>
              </div>

              {/* Shop Section */}
              {(event.extraUrl1 || event.extraUrl2 || event.extraUrl3) && (
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-5 md:p-6 border-l-4 border-green-500 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center mb-3 sm:mb-4">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                      <ShoppingCart className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 break-words">Shop</h3>
                  </div>
                  
                  <div className="space-y-2 sm:space-y-3">
                    {event.extraUrl1 && (
                      <div className="flex items-center p-2.5 sm:p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors duration-200">
                        <Globe className="w-4 h-4 mr-2 sm:mr-3 text-green-500 flex-shrink-0" />
                        <a 
                          href={event.extraUrl1} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-xs sm:text-sm text-gray-900 hover:text-green-600 transition-colors duration-200 break-all"
                        >
                          {event.extraUrl1}
                        </a>
                      </div>
                    )}
                    
                    {event.extraUrl2 && (
                      <div className="flex items-center p-2.5 sm:p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors duration-200">
                        <Globe className="w-4 h-4 mr-2 sm:mr-3 text-green-500 flex-shrink-0" />
                        <a 
                          href={event.extraUrl2} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-xs sm:text-sm text-gray-900 hover:text-green-600 transition-colors duration-200 break-all"
                        >
                          {event.extraUrl2}
                        </a>
                      </div>
                    )}
                    
                    {event.extraUrl3 && (
                      <div className="flex items-center p-2.5 sm:p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors duration-200">
                        <Globe className="w-4 h-4 mr-2 sm:mr-3 text-green-500 flex-shrink-0" />
                        <a 
                          href={event.extraUrl3} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-xs sm:text-sm text-gray-900 hover:text-green-600 transition-colors duration-200 break-all"
                        >
                          {event.extraUrl3}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Information Section */}
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-5 md:p-6 border-l-4 border-purple-500 hover:shadow-xl transition-all duration-300">
                <div className="flex items-center mb-3 sm:mb-4">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                    <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 break-words">Contact Information</h3>
                </div>
                
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex items-center p-2.5 sm:p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors duration-200">
                    <Mail className="w-4 h-4 mr-2 sm:mr-3 text-purple-500 flex-shrink-0" />
                    <a href="mailto:beatsofredmond@gmail.com" className="text-xs sm:text-sm text-gray-900 hover:text-purple-600 transition-colors duration-200 break-all">
                      beatsofredmond@gmail.com
                    </a>
                  </div>
                  
                  <div className="flex items-center p-2.5 sm:p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors duration-200">
                    <Phone className="w-4 h-4 mr-2 sm:mr-3 text-purple-500 flex-shrink-0" />
                    <a href="tel:+12063699576" className="text-xs sm:text-sm text-gray-900 hover:text-purple-600 transition-colors duration-200">
                      (206) 369-9576
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Event Photos & Gallery Section */}
          {((eventPhotos && eventPhotos.length > 0) || (event?.gallery && event.gallery.length > 0)) && (
            <div className="mt-8 sm:mt-12">
              <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8">
                <FeaturedGallery 
                  photos={eventPhotos.length > 0 ? eventPhotos : event.gallery} 
                  title="Event Photos & Gallery" 
                  description={`Highlights and captured moments from ${event.title}`}
                />
              </div>
            </div>
          )}

          {/* Event Sponsors & Community Partners Section */}
          {sponsors && sponsors.length > 0 && (
            <div className="mt-8 sm:mt-12 bg-white rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 border border-gray-100">
              <div className="text-center max-w-2xl mx-auto mb-6 sm:mb-8">
                <div className="inline-flex items-center gap-2 px-3.5 py-1 bg-amber-50 text-amber-800 rounded-full text-xs font-bold mb-2">
                  <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                  <span>Community Partners</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Proud Sponsors & Partners</h3>
                <p className="text-sm sm:text-base text-gray-600 mt-1">Special thanks to our sponsors empowering Beats of Washington</p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6 items-center">
                {sponsors.map((sponsor) => (
                  <a
                    key={sponsor.id || sponsor._id}
                    href={sponsor.websiteUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group p-4 bg-gray-50 hover:bg-white rounded-2xl border border-gray-200 hover:border-orange-400 transition-all duration-300 shadow-sm hover:shadow-xl flex flex-col items-center justify-center text-center transform hover:-translate-y-1"
                  >
                    {sponsor.logoUrl ? (
                      <img
                        src={sponsor.logoUrl}
                        alt={sponsor.name}
                        className="h-14 sm:h-16 w-auto object-contain group-hover:scale-105 transition-transform duration-300 mb-2"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center text-white font-bold text-lg mb-2 shadow-md">
                        {sponsor.name ? sponsor.name.charAt(0) : 'S'}
                      </div>
                    )}
                    {!sponsor.name || /^\d{10,}_|^[0-9a-zA-Z_-]{20,}$|\.jpg|\.png|\.jpeg|\.webp/i.test(sponsor.name) ? null : (
                      <span className="text-xs sm:text-sm font-bold text-gray-900 group-hover:text-orange-600 transition-colors line-clamp-1 mt-1">
                        {sponsor.name}
                      </span>
                    )}
                    {sponsor.tier && (
                      <span className="mt-1 px-2 py-0.5 bg-orange-100 text-orange-800 text-[10px] font-bold rounded-full uppercase tracking-wider">
                        {sponsor.tier}
                      </span>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Embedded Zeffy Registration Modal */}
      {showRegistrationModal && (
        <ZeffyRegistrationModal
          event={event}
          onClose={() => setShowRegistrationModal(false)}
        />
      )}

      {/* Ticket Success Modal */}
      {ticketInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-6 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-300 scale-100 animate-in my-4">
            <div className="p-4 sm:p-5 md:p-6 text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg overflow-hidden">
                {logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt="BOW Logo" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                )}
              </div>
               <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 px-2 break-words">
                 {ticketInfo.registration?.paymentAmount > 0 && ticketInfo.registration?.paymentStatus === 'pending' 
                   ? 'Registration Pending!' 
                   : 'Registration Successful!'
                 }
               </h3>
               <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-2 break-words">
                 {ticketInfo.registration?.paymentAmount > 0 && ticketInfo.registration?.paymentStatus === 'pending'
                   ? `Your registration for ${ticketInfo.registration?.quantity || 1} ${(ticketInfo.registration?.quantity || 1) === 1 ? 'person' : 'people'} is being processed. You will receive a confirmation email once payment is confirmed.`
                   : `Your ticket for ${ticketInfo.registration?.quantity || 1} ${(ticketInfo.registration?.quantity || 1) === 1 ? 'person' : 'people'} has been generated and sent to your email.`
                 }
               </p>
              
               {/* Payment Information for Paid Events */}
               {ticketInfo.registration?.paymentAmount > 0 && (
                 <div className={`rounded-lg p-4 sm:p-5 mb-3 sm:mb-4 border-2 ${
                   ticketInfo.registration.paymentStatus === 'pending' 
                     ? 'bg-yellow-50 border-yellow-300' 
                     : 'bg-blue-50 border-blue-300'
                 }`}>
                   <div className="flex items-center justify-center mb-3">
                     <CreditCard className={`w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0 ${
                       ticketInfo.registration.paymentStatus === 'pending' ? 'text-yellow-600' : 'text-blue-600'
                     }`} />
                     <span className={`text-base sm:text-lg font-bold break-words ${
                       ticketInfo.registration.paymentStatus === 'pending' ? 'text-yellow-900' : 'text-blue-900'
                     }`}>
                       {ticketInfo.registration.paymentStatus === 'pending' ? 'Payment Processing' : 'Payment Confirmed'}
                     </span>
                   </div>
                   
                   {/* Itemized Breakdown */}
                   <div className="bg-white rounded-lg p-3 sm:p-4 mb-3 border border-gray-200">
                     <div className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 pb-2 border-b border-gray-200">
                       Payment Breakdown
                     </div>
                     
                     {/* Event Ticket */}
                     <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-100">
                       <div>
                         <div className="text-sm font-medium text-gray-900">Event Ticket</div>
                         <div className="text-xs text-gray-500">
                           {ticketInfo.registration.quantity} {ticketInfo.registration.quantity === 1 ? 'person' : 'people'} × ${parseEventPrice(event.price).toFixed(2)}
                         </div>
                       </div>
                       <span className="text-sm font-semibold text-gray-900">
                         ${(parseEventPrice(event.price) * ticketInfo.registration.quantity).toFixed(2)}
                       </span>
                     </div>
                     
                     {/* Addon Items */}
                     {registrationAddons && registrationAddons.length > 0 && (
                       <>
                         {registrationAddons.map((addon) => (
                           <div key={addon.id} className="flex justify-between items-center mb-2 pb-2 border-b border-gray-100">
                             <div>
                               <div className="text-sm font-medium text-gray-900">
                                 {addon.name}
                                 {addon.isFreeWithTicket && (
                                   <span className="ml-2 text-xs text-green-600 font-semibold">(FREE)</span>
                                 )}
                               </div>
                               <div className="text-xs text-gray-500">
                                 Quantity: {addon.quantity}
                               </div>
                             </div>
                             <span className="text-sm font-semibold text-gray-900">
                               {addon.isFreeWithTicket ? (
                                 <span className="text-green-600">$0.00</span>
                               ) : (
                                 `$${(addon.price * addon.quantity).toFixed(2)}`
                               )}
                             </span>
                           </div>
                         ))}
                       </>
                     )}
                     
                     {/* Total */}
                     <div className="flex justify-between items-center pt-2 border-t-2 border-gray-300 mt-2">
                       <span className="text-base font-bold text-gray-900">Total Amount:</span>
                       <span className="text-lg font-bold text-green-700">
                         ${(ticketInfo.registration.paymentAmount / 100).toFixed(2)}
                       </span>
                     </div>
                   </div>
                   
                   {ticketInfo.registration.paymentIntentId && (
                     <div className="text-center">
                       <p className={`text-xs break-all ${
                         ticketInfo.registration.paymentStatus === 'pending' ? 'text-yellow-600' : 'text-blue-600'
                       }`}>
                         Transaction ID: {ticketInfo.registration.paymentIntentId.slice(-12)}
                       </p>
                     </div>
                   )}
                 </div>
               )}
              
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 sm:p-5 md:p-6 mb-4 sm:mb-5 md:mb-6 border border-green-200">
                <div className="flex items-center justify-center space-x-2 sm:space-x-3 flex-wrap gap-2">
                  <Ticket className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0" />
                  <p className="text-lg sm:text-xl font-mono text-green-700 font-bold break-all">{ticketInfo.ticketNumber}</p>
                  <button
                    onClick={copyTicketToClipboard}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-green-600 transition-all duration-200 hover:bg-green-100 rounded-lg flex-shrink-0"
                    title="Copy ticket number"
                    aria-label="Copy ticket number"
                  >
                    {copiedTicket ? (
                      <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                  </button>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 mt-2 break-words">Click the copy button to save your ticket number</p>
              </div>
              
               <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                 {ticketInfo.registration?.paymentAmount > 0 && ticketInfo.registration?.paymentStatus === 'completed' && (
                   <button
                     onClick={() => generateReceipt()}
                     className="flex-1 py-2.5 sm:py-3 px-4 sm:px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base font-semibold shadow-lg transform hover:scale-105 transition-all duration-200"
                   >
                     Download Receipt
                   </button>
                 )}
              <button
                onClick={() => setTicketInfo(null)}
                   className={`py-2.5 sm:py-3 px-4 sm:px-6 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 text-sm sm:text-base font-semibold shadow-lg transform hover:scale-105 transition-all duration-200 ${ticketInfo.registration?.paymentAmount > 0 && ticketInfo.registration?.paymentStatus === 'completed' ? 'flex-1' : 'w-full'}`}
              >
                Close
              </button>
               </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EventDetailsPage; 